import { Clock } from "lucide-react";
import { AssessmentAudit } from "../types";

interface LastModifiedProps {
  audit: AssessmentAudit | undefined;
  className?: string;
}

/**
 * Formats an audit timestamp for display.
 *
 * Exported for tests and for callers that need the text without the icon.
 * Returns null for anything unparseable so a bad timestamp degrades to no line
 * at all rather than "Invalid Date".
 */
export function formatLastModified(
  audit: AssessmentAudit | undefined,
): string | null {
  if (!audit?.lastModifiedAt) return null;

  const when = new Date(audit.lastModifiedAt);
  if (Number.isNaN(when.getTime())) return null;

  const date = when.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  // The backend leaves the name empty when the account behind the change has
  // been removed. The date is still a useful audit fact on its own, so show it
  // rather than inventing an attribution.
  return audit.lastModifiedBy
    ? `Last modified by ${audit.lastModifiedBy} on ${date}`
    : `Last modified on ${date}`;
}

/**
 * One-line audit trail for an assessment: when it was last changed and by whom.
 *
 * Renders nothing when there is no audit information, so callers can drop it in
 * unconditionally without guarding each site.
 */
export default function LastModified({ audit, className }: LastModifiedProps) {
  const text = formatLastModified(audit);
  if (!text) return null;

  return (
    <p
      className={`flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 ${className ?? ""}`}
    >
      <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span>{text}</span>
    </p>
  );
}
